'use client';

import { SESSION_TYPE, useAccountUm } from '@/hooks/use-account-um';
import { useStore } from '@/store/store';
import type { Region } from '@/types/constants';
import { COOKIES, setCookie, removeCookie } from '@/utils/cookies';
import { getRegionSelectedProps, getStateSelectedProps } from '@/tracking/amplitude/events/general/state-selector';
import { getPathFromReferrer } from '@/utils/url/url';
import { getPostLoginAction } from '@/utils/post-login-action/post-login-action';

type StateSelectorRegionProps = {
    region: Region;
    children: React.ReactNode;
};

export const StateSelectorRegion: React.FC<StateSelectorRegionProps> = ({ children, region }) => {
    const { activePopup, closePopup, onCloseAction } = useStore((state) => state.popup);
    const { params } = activePopup!;
    const handleShowLoading = useStore((state) => state.loading.handleShowLoading);

    const { sendEvent } = useStore((state) => state.tracking);

    const { getSessionWithLoginView, getSessionWithRegisterView } = useAccountUm();

    const handleSession = async () => {
        closePopup();
        // Read before login completes: PostLoginHandler clears sessionStorage once logged in.
        const hasPendingPostLoginAction = getPostLoginAction() !== null;

        const session =
            params.type === SESSION_TYPE.LOGIN ? await getSessionWithLoginView() : await getSessionWithRegisterView();

        if (session) {
            removeCookie(COOKIES.SHOULD_DISPLAY_REGION_SELECTOR);

            if (hasPendingPostLoginAction) {
                // useAccountUm.handleSession (session subscription) does a hard
                // window.location.reload() once cookies are synced when a
                // post-login action is pending — that gives PostLoginHandler a
                // fully-server-rendered logged-in page to consume the action
                // on. We skip the local reload/href-assignment below to avoid
                // a duplicate navigation.
                return;
            }

            handleShowLoading();

            const path = getPathFromReferrer(params.externalReferrer);
            if (path) {
                window.location.href = path;
            } else {
                window.location.reload();
            }
        }
    };

    const handleClick = async () => {
        setCookie(COOKIES.REGION, region);

        const isRegionSwitch = params.source === 'region_switch';
        const eventProps = isRegionSwitch ? getRegionSelectedProps(region) : getStateSelectedProps();
        sendEvent([{ destination: 'amplitude', eventProps }]);

        if (typeof onCloseAction === 'function') {
            closePopup();
            onCloseAction();
            return;
        }

        const { refreshConfig } = await import('@/logic/unified-modules');
        await refreshConfig();

        await handleSession();
    };

    return (
        // TODO: Refactor this and make it a button
        <div onKeyDown={() => undefined} onClick={handleClick}>
            {children}
        </div>
    );
};
